import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Button, Input, Spinner, Switch } from '@components/ui';
import { TEST_IDS } from '@lib/utils/testIds';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';
import { useDashboardMetrics, useDashboardCharts, useDashboardRankings } from '../hooks/useDashboard';
import { useAuthStore } from '@features/auth/store/authStore';
import { formatNumber } from '@/utils/format';
import { useInvoiceFilterStore } from '@/stores/invoiceFilterStore';
import { useSelectedCompany } from '@features/company/hooks/useSelectedCompany';
import { PromoDecimoBanner } from './PromoDecimoBanner';
import { companyService } from '@features/companies/services/companyService';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { MonthlyChartData, StatusDistributionData } from '../types/dashboard.types';
import { canAccessDashboard } from '@lib/utils/roleNavigation';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Redirect restricted roles to Facturas Activas
  useEffect(() => {
    if (user?.role && !canAccessDashboard(user.role)) {
      navigate('/home/main', { replace: true });
    }
  }, [user?.role, navigate]);

  // Calcular el primer día del mes actual
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  // Global filter state (persists across views)
  const { hideErrorInvoices, setHideErrorInvoices } = useInvoiceFilterStore();

  const { data: selectedCompanyData, isLoading: companyLoading, refetch: refetchSelectedCompany } = useSelectedCompany();
  // Handle both data shapes: the query cache may contain either the unwrapped
  // Company object ({ id, name }) or the wrapped response ({ company: { id, name } })
  // because TenantSwitcher uses the same queryKey with a different queryFn.
  const companyId = (selectedCompanyData as any)?.company?.id || selectedCompanyData?.id;
  const hasCompany = !!companyId;
  const [autoSelecting, setAutoSelecting] = useState(false);

  // Auto-select first company if none is selected (skip for super admin — they use CompanySelectorPage)
  const isSuperAdmin = (user as any)?.isSuperAdmin === true;
  useEffect(() => {
    if (!hasCompany && !companyLoading && !autoSelecting && !isSuperAdmin) {
      setAutoSelecting(true);
      companyService.getCompanies()
        .then((data) => {
          const companies = data?.companies || [];
          if (companies.length > 0) {
            return companyService.selectCompany(companies[0].id);
          }
        })
        .then(() => refetchSelectedCompany())
        .catch((err) => console.error('Auto-select company failed:', err))
        .finally(() => setAutoSelecting(false));
    }
  }, [hasCompany, companyLoading, autoSelecting, isSuperAdmin, refetchSelectedCompany]);

  const { data: metricsResponse, isLoading, error } = useDashboardMetrics(startDate, endDate, hasCompany);
  const { data: chartsResponse, isLoading: chartsLoading } = useDashboardCharts(6, hasCompany);
  const { data: rankingsResponse, isLoading: rankingsLoading } = useDashboardRankings(5, hasCompany);

  const metrics = useMemo(() => {
    return metricsResponse?.data?.[0];
  }, [metricsResponse]);

  // Process chart data
  const { monthlyData, statusData } = useMemo(() => {
    if (!chartsResponse?.data) {
      return { monthlyData: [], statusData: [] };
    }

    const monthly = chartsResponse.data.filter(
      (item): item is MonthlyChartData => item.dataset_type === 'monthly'
    ).sort((a, b) => {
      // Sort by year and month
      const yearDiff = parseInt(a.year) - parseInt(b.year);
      if (yearDiff !== 0) return yearDiff;
      return parseInt(a.month_num) - parseInt(b.month_num);
    });

    // Define a consistent blue palette
    const blueColors = [
      '#3b82f6', // Blue-500 (primary)
      '#60a5fa', // Blue-400 (lighter)
      '#93c5fd', // Blue-300 (even lighter)
      '#2563eb', // Blue-600 (darker)
      '#1d4ed8', // Blue-700 (darkest)
      '#bfdbfe', // Blue-200 (very light)
    ];

    const status = chartsResponse.data.filter(
      (item): item is StatusDistributionData => item.dataset_type === 'status_distribution'
    ).map((item, index) => ({
      name: item.month,
      value: item.sort_order,
      color: blueColors[index % blueColors.length], // Use blue palette instead of original colors
    }));

    return { monthlyData: monthly, statusData: status };
  }, [chartsResponse]);

  // Process rankings data
  const { topCustomers, topDocuments } = useMemo(() => {
    if (!rankingsResponse?.data) {
      return { topCustomers: [], topDocuments: [] };
    }

    const customers = rankingsResponse.data
      .filter(item => item.ranking_type === 'top_customers')
      .sort((a, b) => a.rank_order - b.rank_order)
      .slice(0, 5);
    const documents = rankingsResponse.data
      .filter(item => item.ranking_type === 'top_documents')
      .sort((a, b) => a.rank_order - b.rank_order)
      .slice(0, 5);

    return { topCustomers: customers, topDocuments: documents };
  }, [rankingsResponse]);

  const stats = useMemo(() => {
    if (!metrics) return [];

    return [
      {
        name: 'Total Facturas',
        value: formatNumber(metrics.total_invoices, 0),
        icon: DocumentTextIcon,
        change: `${metrics.total_invoices_change_pct > 0 ? '+' : ''}${formatNumber(metrics.total_invoices_change_pct, 2)}%`,
        changeType: metrics.total_invoices_change_pct >= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.TOTAL_INVOICES,
      },
      {
        name: 'Ingresos del Período',
        value: `$${formatNumber(metrics.monthly_revenue)}`,
        icon: CurrencyDollarIcon,
        change: `${metrics.monthly_revenue_change_pct > 0 ? '+' : ''}${formatNumber(metrics.monthly_revenue_change_pct, 2)}%`,
        changeType: metrics.monthly_revenue_change_pct >= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.REVENUE,
      },
      {
        name: 'Facturas Pendientes',
        value: formatNumber(metrics.pending_invoices, 0),
        icon: ClockIcon,
        change: `${metrics.pending_invoices_change_pct > 0 ? '+' : ''}${formatNumber(metrics.pending_invoices_change_pct, 2)}%`,
        changeType: metrics.pending_invoices_change_pct <= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.PENDING_INVOICES,
      },
      {
        name: 'Facturas Aprobadas',
        value: formatNumber(metrics.approved_invoices, 0),
        icon: CheckCircleIcon,
        change: `${metrics.approved_invoices_change_pct > 0 ? '+' : ''}${formatNumber(metrics.approved_invoices_change_pct, 2)}%`,
        changeType: metrics.approved_invoices_change_pct >= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.APPROVED_INVOICES,
      },
      {
        name: 'Facturas Rechazadas',
        value: formatNumber(metrics.rejected_invoices, 0),
        icon: XCircleIcon,
        change: 'N/A',
        changeType: 'neutral' as const,
        description: 'del período',
        testId: TEST_IDS.DASHBOARD.STATS.REJECTED_INVOICES,
      },
      {
        name: 'Clientes Activos',
        value: formatNumber(metrics.active_customers, 0),
        icon: UserGroupIcon,
        change: `${metrics.active_customers_change_pct > 0 ? '+' : ''}${formatNumber(metrics.active_customers_change_pct, 2)}%`,
        changeType: metrics.active_customers_change_pct >= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.ACTIVE_CUSTOMERS,
      },
      {
        name: 'Tipos de Documentos',
        value: formatNumber(metrics.document_types, 0),
        icon: DocumentChartBarIcon,
        change: 'N/A',
        changeType: 'neutral' as const,
        description: 'tipos diferentes',
        testId: TEST_IDS.DASHBOARD.STATS.DOCUMENT_TYPES,
      },
      {
        name: 'Promedio por Factura',
        value: `$${formatNumber(metrics.average_invoice)}`,
        icon: ArrowTrendingUpIcon,
        change: `${metrics.average_invoice_change_pct > 0 ? '+' : ''}${formatNumber(metrics.average_invoice_change_pct, 2)}%`,
        changeType: metrics.average_invoice_change_pct >= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.AVERAGE_INVOICE,
      },
    ];
  }, [metrics]);

  // companyLoading = true only on first fetch (data is undefined)
  // Once data is null or an object, companyLoading = false
  if (companyLoading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Dashboard</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Resumen general de tu actividad de facturación electrónica
          </p>
        </div>
        <div className="flex justify-center py-12">
          <Spinner className="w-12 h-12" />
        </div>
      </div>
    );
  }

  if (!hasCompany) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Dashboard</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Resumen general de tu actividad de facturación electrónica
          </p>
        </div>
        <Card>
          <CardBody className="p-12 text-center">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400 mb-4" />
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                  No tienes compañías asignadas
                </h3>
                <p className="text-secondary-600 dark:text-secondary-400">
                  Contacta a tu administrador para que te asigne una compañía.
                </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Dashboard</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Resumen general de tu actividad de facturación electrónica
          </p>
        </div>
        <Card>
          <CardBody className="p-12 text-center">
            <XCircleIcon className="mx-auto h-12 w-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Error al cargar métricas
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              {error instanceof Error ? error.message : 'Ocurrió un error inesperado'}
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid={TEST_IDS.DASHBOARD.CONTAINER}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Dashboard</h1>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Resumen general de tu actividad de facturación electrónica
        </p>
      </div>

      <PromoDecimoBanner />

      {/* Date Range Selector */}
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
            <div className="flex-1 w-full">
              <Input
                type="date"
                label="Fecha Inicio"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid={TEST_IDS.DASHBOARD.START_DATE_INPUT}
              />
            </div>
            <div className="flex-1 w-full">
              <Input
                type="date"
                label="Fecha Fin"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid={TEST_IDS.DASHBOARD.END_DATE_INPUT}
              />
            </div>
            <Button
              data-guide="Actualizar Dashboard"
              className="w-full sm:w-auto"
              onClick={() => {
                // Trigger refetch by updating dates
                const newEndDate = new Date().toISOString().split('T')[0];
                setEndDate(newEndDate);
              }}
              disabled={isLoading}
              data-testid={TEST_IDS.DASHBOARD.UPDATE_BUTTON}
            >
              Actualizar
            </Button>
          </div>
          <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Switch
              checked={hideErrorInvoices}
              onChange={setHideErrorInvoices}
              label="Ocultar facturas con error en listas"
              size="sm"
            />
          </div>
        </CardBody>
      </Card>

      {isLoading ? (
        <div className="flex justify-center items-center py-24" data-testid={TEST_IDS.DASHBOARD.LOADING}>
          <Spinner className="w-12 h-12" />
        </div>
      ) : metrics ? (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
              <Card key={stat.name} data-testid={stat.testId}>
                <CardBody className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <stat.icon className="h-5 w-5 text-primary-600" />
                        <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          {stat.name}
                        </p>
                      </div>
                      <p className="mt-3 text-2xl font-bold text-secondary-900 dark:text-white">
                        {stat.value}
                      </p>
                      <div className="mt-2 flex items-center gap-1">
                        <span
                          className={`text-sm font-medium ${
                            stat.changeType === 'positive'
                              ? 'text-success-600'
                              : stat.changeType === 'negative'
                              ? 'text-danger-600'
                              : 'text-secondary-500'
                          }`}
                        >
                          {stat.change}
                        </span>
                        <span className="text-xs text-secondary-500">{stat.description}</span>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

          {/* Resumen Adicional */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <Card>
              <CardBody>
                <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Tasa de Aprobación
                </h3>
                <p className="mt-2 text-3xl font-bold text-success-600">
                  {formatNumber(metrics.approval_rate, 2)}%
                </p>
                <p className="mt-1 text-xs text-secondary-500">
                  {formatNumber(metrics.approved_invoices, 0)} de{' '}
                  {formatNumber(metrics.total_invoices, 0)} facturas aprobadas
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Tiempo Promedio de Aprobación
                </h3>
                <p className="mt-2 text-3xl font-bold text-primary-600">
                  {formatNumber(metrics.avg_approval_time_hours, 1)} hrs
                </p>
                <p className="mt-1 text-xs text-secondary-500">Desde emisión hasta aprobación</p>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Facturación Proyectada
                </h3>
                <p className="mt-2 text-3xl font-bold text-primary-600">
                  ${formatNumber(metrics.projected_revenue)}
                </p>
                <p className="mt-1 text-xs text-secondary-500">Estimado para fin de período</p>
              </CardBody>
            </Card>
          </div>

          {/* Charts Section */}
          {!chartsLoading && monthlyData.length > 0 && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Monthly Invoices Chart */}
              <Card data-testid={TEST_IDS.DASHBOARD.CHARTS.MONTHLY}>
                <CardHeader>
                  <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Facturación Mensual
                  </h2>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Últimos 6 meses
                  </p>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.5}
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        stroke="#9ca3af"
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        stroke="#9ca3af"
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                        }}
                        labelStyle={{ color: '#111827', fontWeight: 600 }}
                        itemStyle={{ color: '#3b82f6' }}
                      />
                      <Legend
                        wrapperStyle={{
                          paddingTop: '20px',
                          color: '#374151'
                        }}
                      />
                      <Bar
                        dataKey="invoices"
                        fill="#3b82f6"
                        name="Facturas"
                        radius={[8, 8, 0, 0]}
                        label={{
                          position: 'top',
                          fill: '#374151',
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>

              {/* Status Distribution Pie Chart */}
              <Card data-testid={TEST_IDS.DASHBOARD.CHARTS.STATUS_DISTRIBUTION}>
                <CardHeader>
                  <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Distribución por Estado
                  </h2>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Estado actual de facturas
                  </p>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={{
                          stroke: '#9ca3af',
                          strokeWidth: 1
                        }}
                        label={(entry) => {
                          return `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`;
                        }}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        stroke="#ffffff"
                        strokeWidth={2}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                        }}
                        itemStyle={{ color: '#374151', fontWeight: 600 }}
                      />
                      <Legend
                        wrapperStyle={{
                          paddingTop: '20px',
                        }}
                        iconType="circle"
                        formatter={(value) => (
                          <span style={{ color: '#374151', fontSize: '12px' }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>
          )}

          {/* Rankings Tables */}
          {!rankingsLoading && (topCustomers.length > 0 || topDocuments.length > 0) && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Top Customers Table */}
              {topCustomers.length > 0 && (
                <Card data-testid={TEST_IDS.DASHBOARD.TABLES.TOP_CUSTOMERS}>
                  <CardHeader>
                    <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                      Top 5 Clientes
                    </h2>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      Por facturación total
                    </p>
                  </CardHeader>
                  <CardBody>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Cliente
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Facturas
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Total
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Promedio
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                          {topCustomers.map((customer, index) => (
                            <tr key={index} className="hover:bg-secondary-50 dark:hover:bg-secondary-800">
                              <td className="px-4 py-3 text-sm">
                                <div className="font-medium text-secondary-900 dark:text-white">
                                  {customer.name}
                                </div>
                                {customer.identifier && (
                                  <div className="text-xs text-secondary-500">
                                    {customer.identifier}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-secondary-900 dark:text-white">
                                {formatNumber(customer.quantity, 0)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-secondary-900 dark:text-white">
                                ${formatNumber(customer.amount)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-secondary-600 dark:text-secondary-400">
                                ${formatNumber(customer.average)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Top Documents Table */}
              {topDocuments.length > 0 && (
                <Card data-testid={TEST_IDS.DASHBOARD.TABLES.TOP_DOCUMENTS}>
                  <CardHeader>
                    <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                      Top 5 Tipos de Documento
                    </h2>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      Por cantidad y monto
                    </p>
                  </CardHeader>
                  <CardBody>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Tipo
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Cantidad
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Total
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                              Promedio
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                          {topDocuments.map((doc, index) => (
                            <tr key={index} className="hover:bg-secondary-50 dark:hover:bg-secondary-800">
                              <td className="px-4 py-3 text-sm font-medium text-secondary-900 dark:text-white">
                                {doc.name}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-secondary-900 dark:text-white">
                                {formatNumber(doc.quantity, 0)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-secondary-900 dark:text-white">
                                ${formatNumber(doc.amount)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-secondary-600 dark:text-secondary-400">
                                ${formatNumber(doc.average)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardBody className="p-12 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400 mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              No hay datos disponibles
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              Selecciona un rango de fechas para ver las métricas
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
